import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, X, CheckCircle2, Loader2, Paperclip } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const MAX = 10 * 1024 * 1024;
const OK_EXT = ["pdf", "docx", "jpg", "jpeg", "png"];

export function ApplicationForm({ job = null, branches = [], onDone }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    desired_branch_id: job?.branch_id || "", message: "", consent: false,
  });
  const [files, setFiles] = useState([]); // {file_id, name, size}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    for (const file of arr) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!OK_EXT.includes(ext)) { toast.error(`${file.name}: nur PDF, DOCX, JPG, PNG erlaubt`); continue; }
      if (file.size > MAX) { toast.error(`${file.name}: max. 10 MB`); continue; }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/careers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      } catch (e) {
        toast.error(formatError(e.response?.data?.detail));
      } finally { setUploading(false); }
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.file_id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Bitte stimmen Sie der Datenverarbeitung zu."); return; }
    setSubmitting(true);
    try {
      await api.post("/careers/apply", {
        job_id: job?.id || null,
        is_initiative: !job,
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone,
        desired_branch_id: form.desired_branch_id || null,
        message: form.message, consent: true,
        file_ids: files.map((f) => f.file_id),
      });
      setDone(true);
      if (onDone) onDone();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="application-success">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
        <h3 className="text-2xl font-bold mt-4">Vielen Dank für Ihre Bewerbung!</h3>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          Wir haben Ihre Unterlagen erhalten und eine Bestätigung an Ihre E-Mail-Adresse gesendet.
          Unser Team meldet sich in Kürze bei Ihnen.
        </p>
        <Link to="/karriere" className="inline-block mt-6 px-6 py-3 rounded-xl bg-[#D62B3A] text-white font-semibold" data-testid="back-to-careers">
          Zurück zur Karriereseite
        </Link>
      </div>
    );
  }

  const inp = "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#D62B3A] focus:ring-1 focus:ring-[#D62B3A] outline-none";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="application-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Vorname *" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inp} data-testid="app-first-name" />
        <input required placeholder="Nachname *" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inp} data-testid="app-last-name" />
        <input required type="email" placeholder="E-Mail *" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="app-email" />
        <input required placeholder="Telefonnummer *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="app-phone" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700">Gewünschte Stelle</label>
        <input disabled value={job ? `${job.title} – ${job.branch_name}` : "Initiativbewerbung"} className={`${inp} bg-gray-50 mt-1`} data-testid="app-desired-job" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700">Gewünschter Standort *</label>
        <select required value={form.desired_branch_id} onChange={(e) => set("desired_branch_id", e.target.value)} className={`${inp} mt-1`} data-testid="app-branch">
          <option value="">Bitte wählen…</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <textarea placeholder="Nachricht / Anschreiben" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={inp} data-testid="app-message" />

      <div>
        <label className="text-sm font-medium text-gray-700">Lebenslauf & Anhänge (PDF, DOCX, JPG, PNG · max. 10 MB)</label>
        <label className="mt-1 flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#D62B3A] cursor-pointer transition-colors" data-testid="app-upload-zone">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-gray-400" />}
          <span className="text-sm text-gray-500">{uploading ? "Wird hochgeladen…" : "Dateien auswählen oder hierher ziehen"}</span>
          <input type="file" multiple accept=".pdf,.docx,.jpg,.jpeg,.png" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="app-file-input" />
        </label>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1">
            {files.map((f) => (
              <li key={f.file_id} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                <span className="flex items-center gap-2 truncate"><Paperclip className="w-4 h-4 text-gray-400" /> {f.name}</span>
                <button type="button" onClick={() => removeFile(f.file_id)} className="text-gray-400 hover:text-[#D62B3A]" data-testid="app-remove-file"><X className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <label className="flex items-start gap-3 text-sm text-gray-600">
        <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[#D62B3A]" data-testid="app-consent" />
        <span>Ich willige ein, dass meine Bewerbungsdaten gemäß der <Link to="/datenschutz-bewerber" target="_blank" className="text-[#D62B3A] underline">Datenschutzerklärung für Bewerber</Link> verarbeitet und gespeichert werden. *</span>
      </label>

      <button type="submit" disabled={submitting || uploading} className="w-full py-3.5 rounded-xl bg-[#D62B3A] hover:bg-[#b8232f] text-white font-semibold text-lg disabled:opacity-60 transition-colors" data-testid="app-submit">
        {submitting ? "Wird gesendet…" : "Bewerbung absenden"}
      </button>
    </form>
  );
}
